import { useForm } from "react-hook-form";
import { useCreateBulkMedicationReportMutation } from "../../../services/api/medicationsApi";
import { useDisplayToast } from "ui/hooks/useDisplayToast";
import { AdministerMedicationRequest } from "../../../services/api/types/medications/requests/AdministerMedicationRequest";
import { BulkMedicationsFormFields } from "../../../services/api/types/medications/models/BulkMedicationsFormFields";
import { MedicationAdministrationStatusEnum } from "../../../services/api/types/medications/enums/MedicationAdministrationStatusEnum";
import { AdministerBulkMedicationRequest } from "../../../services/api/types/medications/requests/AdministerBulkMedicationRequest";
import { MedicationModel } from "../../../services/api/types/medications/models/MedicationModel";
import { useEffect, useMemo } from "react";

export function useAdministerBulkMedication(
  data?: MedicationModel[],
  options?: { onFinishedBulkAdminister?: () => void },
) {
  const [triggerAdministerBulkMedication] =
    useCreateBulkMedicationReportMutation();

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
    reset,
    getValues,
  } = useForm<BulkMedicationsFormFields>({
    defaultValues: { medications: {} },
  });

  useEffect(() => {
    if (data) {
      const currentValues = getValues()?.medications;

      const medications = data?.reduce<
        Record<string, AdministerMedicationRequest>
      >((acc, med) => {
        acc[`${med.id}`] = {
          resident_id: med.resident_id,
          medication_id: med.id,
          dosage: currentValues?.[med.id]?.dosage || med.dosage,
          other_information: currentValues?.[med.id]?.other_information || "",
          status:
            currentValues?.[med.id]?.status ||
            MedicationAdministrationStatusEnum.NONE,
          skip_reason: currentValues?.[med.id]?.skip_reason || "",
        };

        return acc;
      }, {});

      reset({
        medications,
      });
    }
  }, [data, reset]);

  const { showNewToast, showErrorToast } = useDisplayToast();

  const administerBulkMedication = handleSubmit(
    async (formData: BulkMedicationsFormFields) => {
      try {
        if (!data || !data.length) {
          return;
        }

        const bulkMeds = Object.values(formData.medications);
        const payload: AdministerBulkMedicationRequest = bulkMeds
          .filter(
            (item) =>
              item.status !== undefined &&
              item.status !== MedicationAdministrationStatusEnum.NONE,
          )
          .map((item) => ({
            resident_id: item.resident_id,
            medication_id: item.medication_id,
            dosage: item.dosage || "0",
            other_information: item.other_information || "",
            status: item.status,
            skip_reason: item.skip_reason || "",
          }));

        if (!payload.length) {
          showNewToast({
            title: "No medication selected",
            message: "No medications were marked to be logged",
            type: "error",
          });
          if (options?.onFinishedBulkAdminister)
            options.onFinishedBulkAdminister();

          return;
        }

        await triggerAdministerBulkMedication({ body: payload }).unwrap();

        showNewToast({
          title: "Medications Administered",
          message: "Successfully administered medications",
          type: "success",
        });

        reset();
        if (options?.onFinishedBulkAdminister)
          options.onFinishedBulkAdminister();
      } catch (error) {
        showErrorToast(error);
      }
    },
  );

  return {
    control,
    administerBulkMedication,
    isSubmitting,
  };
}
